import FullCalendar from "@fullcalendar/react";
import { CalendarOptions } from "@fullcalendar/core";
import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { StyleWrapper } from "./style";
import { Modal } from "./../modal";
import styled from "styled-components";
import { Button } from "./../button";
import { Spacer } from "./../spacer";
import { add, format, isAfter } from "date-fns";

export interface IDatePickerModalProps {
  value?:           Date,
  show?:            boolean,
  calendarProps?:   CalendarOptions,
  range?:           boolean,
  start?:           Date,
  end?:             Date,
  showClearButton?: boolean,
  onClose?       ():                      void,
  onSubmit?     (date: Date):             void,
  onSubmitRange?(start: Date, end: Date): void,

}

export const DatePickerModal = (props: IDatePickerModalProps) => {
  const { showClearButton, show, value, range, onClose, onSubmit, onSubmitRange, calendarProps, start, end} = props;
  const { t, i18n }  = useTranslation();

  const [date,    setDate]    = React.useState<Date>(value  || new Date());
  const [_start,  setStart]   = React.useState<Date>(start  || new Date());
  const [_end,    setEnd]     = React.useState<Date>(end    || new Date());
  const [isStart, setIsStart] = React.useState<boolean>(true);

  useEffect( () => {
    localStorage.setItem('_op_date_picker_modal_open', show.toString())
    return () => {
      localStorage.setItem('_op_date_picker_modal_open', null)
    }
  }, [show]);

  const handleCalendarRef = useCallback( (instance: FullCalendar) => {
    if (!instance) return;
    if(range) {
      instance.getApi().select(_start, add(_end, {days: 1}));
    }
    else if(date) {
      instance.getApi().select(date);
    }
  }, [date, _start, _end]);


  return (
    <DatePickerModalContainer 
      noBorder 
      closeOnEsc
      show    = {show}
      onClose = {() => {
        onClose();
        setDate(new Date(value) || new Date());
      }}
    >
      <StyleWrapper>
        <FullCalendar
          ref                = {handleCalendarRef}
          weekNumberFormat   = {{ week: "numeric" }}
          plugins            = {[dayGridPlugin, interactionPlugin]}
          initialView        = "dayGridMonth"
          handleWindowResize = {true}
          firstDay           = {1}
          aspectRatio        = {1}
          // allDay             = {true}
          height             = {"43rem"}
          timeZone           = "UTC"
          locale             = {i18n.language}
          locales            = {[svLocale, enLocale]}
          dateClick          = {(e) => {
            
            if(!e.date) return;
            // console.log(e.dateStr)
            const date = new Date(e.dateStr);
            // console.log('use', e.dateStr)
            if(range) {
              if(isStart) {
                setStart(date)
                if(isAfter(date, _end)) setEnd(date);
              }
              else {
                setEnd(date);
                if(isAfter(_start, date)) setStart(date);
              }
              setIsStart(!isStart);
            }
            else setDate(date)
          }}
          headerToolbar      = {{
            start:  "title",
            center: "",
            end:    "today prev,next"
          }}
          {...calendarProps}
        />
        <Box
          cols  = {1}
          style = {{
            margin:         "1.5rem 0.5rem",
            display:        "flex",
            justifyContent: "flex-end"
          }}
        >
          <Button variant="outlined" onClick={() => { onClose && onClose() }}>
            {t("cancel")}
          </Button>
          <Spacer />
          {showClearButton && 
            <>
              <Button variant="outlined" onClick={() => { 
                  setDate(null);
                  setEnd(null);
                  setStart(null);
                  setIsStart(true);
                  onSubmit && onSubmit(null); 
                  range    && onSubmitRange && onSubmitRange(null, null);
                  onClose  && onClose();
                }}
              >
                {t("clear")}
              </Button>
              <Spacer />
            </>
          }
          <Button 
            onClick={() => { 
              onSubmit && onSubmit(date); 
              range    && onSubmitRange && onSubmitRange(_start, _end);
              onClose  && onClose(); 
            }}
          >
            OK
          </Button>
        </Box>
      </StyleWrapper>
    </DatePickerModalContainer>
  );
}

const DatePickerModalContainer = styled(Modal)`
  min-width: 25rem;
  padding: 1rem;
  z-index: 10;
`;